import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import { ClientAddressEntity } from './client-address.entity';
import { ClientEntity } from './client.entity';

export type FacilityPersonnelRole =
	| 'pharmacist_in_charge'
	| 'staff_pharmacist'
	| 'technician'
	| 'intern'
	| 'other';

export type FacilityPersonnelLinkSource =
	| 'pic_data'
	| 'npi_address'
	| 'human_added'
	| 'imported';

export type FacilityPersonnelStatus =
	| 'active'
	| 'confirmed'
	| 'removed'
	| 'transferred';

@Index('idx_fp_client', ['fkClientId'])
@Index('idx_fp_address', ['fkClientAddressId'])
@Index('idx_fp_registry', ['registryLicenseId'])
@Index('idx_fp_harvest', ['harvestedAt'])
@Entity('facility_personnel')
export class FacilityPersonnelEntity extends BaseEntity {
	@Column('int', { name: 'fk_client__id', unsigned: true })
	public fkClientId!: number;

	@Column('int', {
		name: 'fk_client_address__id',
		nullable: true,
		unsigned: true,
	})
	public fkClientAddressId!: number | null;

	@Column('int', {
		name: 'registry_license_id',
		nullable: true,
		unsigned: true,
	})
	public registryLicenseId!: number | null;

	@Column('varchar', { name: 'individual_name', length: 500 })
	public individualName!: string;

	@Column('varchar', { name: 'individual_license_type', length: 100 })
	public individualLicenseType!: string;

	@Column('varchar', {
		name: 'individual_license_number',
		nullable: true,
		length: 100,
	})
	public individualLicenseNumber!: string | null;

	@Column('varchar', {
		name: 'individual_npi',
		nullable: true,
		length: 10,
	})
	public individualNpi!: string | null;

	@Column('enum', {
		name: 'role',
		enum: [
			'pharmacist_in_charge',
			'staff_pharmacist',
			'technician',
			'intern',
			'other',
		],
		default: 'other',
	})
	public role!: FacilityPersonnelRole;

	@Column('enum', {
		name: 'link_source',
		enum: ['pic_data', 'npi_address', 'human_added', 'imported'],
	})
	public linkSource!: FacilityPersonnelLinkSource;

	@Column('decimal', {
		name: 'confidence_score',
		nullable: true,
		precision: 5,
		scale: 2,
	})
	public confidenceScore!: string | null;

	@Column('enum', {
		name: 'status',
		enum: ['active', 'confirmed', 'removed', 'transferred'],
		default: 'active',
	})
	public status!: FacilityPersonnelStatus;

	@Column('date', { name: 'effective_date', nullable: true })
	public effectiveDate!: string | null;

	@Column('date', { name: 'end_date', nullable: true })
	public endDate!: string | null;

	@Column('int', {
		name: 'reviewed_by_user_id',
		nullable: true,
		unsigned: true,
	})
	public reviewedByUserId!: number | null;

	@Column('datetime', { name: 'reviewed_at', nullable: true })
	public reviewedAt!: Date | null;

	@Column('datetime', { name: 'harvested_at', nullable: true })
	public harvestedAt!: Date | null;

	@ManyToOne(() => ClientEntity, { onDelete: 'CASCADE', onUpdate: 'RESTRICT' })
	@JoinColumn([{ name: 'fk_client__id', referencedColumnName: 'id' }])
	public fkClient!: ClientEntity;

	@ManyToOne(() => ClientAddressEntity, {
		onDelete: 'SET NULL',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_client_address__id', referencedColumnName: 'id' }])
	public fkClientAddress!: ClientAddressEntity | null;
}
